<template>
  <v-skeleton-loader
    type="text"
    :loading="$apollo.queries.internalHistory.loading"
  >
    <slot :duration-type="durationType" :duration="duration" :item="item">
      <span v-bind="$attrs">{{ duration | humanizeDuration }}</span>
    </slot>
  </v-skeleton-loader>
</template>

<script>
import gql from 'graphql-tag'

export default {
  name: 'HistoryViewDuration',

  props: {
    historieableId: {
      type: Number,
      required: false,
      default: null
    },

    historieableType: {
      type: String,
      required: false,
      default: null
    },

    history: {
      type: Array,
      required: false,
      default: null
    },

    durationType: {
      type: String,
      required: false,
      default: 'SUM',
      validator: (value) => ['SUM', 'NEWEST', 'OLDEST'].includes(value)
    },

    allowedEvents: {
      type: Array,
      required: false,
      default: null
    },

    disallowedEvents: {
      type: Array,
      required: false,
      default: null
    }
  },

  apollo: {
    internalHistory: {
      query: gql`
        query histories($historieable_id: Int!, $historieable_type: String!) {
          internalHistory: histories(
            historieable_id: $historieable_id
            historieable_type: $historieable_type
            orderBy: [{ column: CREATED_AT, order: DESC }]
          ) {
            id
            event
            duration
            created_at
          }
        }
      `,
      variables() {
        return {
          historieable_id: this.historieableId,
          historieable_type: this.historieableType
        }
      },
      skip() {
        return (
          Array.isArray(this.histories) ||
          !this.historieableId ||
          !this.historieableType
        )
      },
      fetchPolicy: 'network-only'
    }
  },

  data() {
    return {
      internalHistory: []
    }
  },

  computed: {
    filteredHistory() {
      let _return = this.internalHistory || []

      if (Array.isArray(this.allowedEvents)) {
        _return = _return.filter((item) =>
          this.allowedEvents.includes(item.event)
        )
      }

      if (Array.isArray(this.disallowedEvents)) {
        _return = _return.filter(
          (item) => !this.disallowedEvents.includes(item.event)
        )
      }

      return _return
    },

    duration() {
      switch (this.durationType) {
        case 'NEWEST':
          return this.newest?.duration ?? 0
        case 'OLDEST':
          return this.oldest?.duration ?? 0
        case 'SUM':
        default:
          return this.durationSum
      }
    },

    item() {
      switch (this.durationType) {
        case 'NEWEST':
          return this.newest
        case 'OLDEST':
          return this.oldest
        case 'SUM':
        default:
          return null
      }
    },

    durationSum() {
      return this.filteredHistory.reduce((acc, cur) => acc + cur.duration, 0)
    },

    newest() {
      // we will assume, that the history is ordered descending by "CREATED_AT"
      return this.filteredHistory?.[0]
    },

    oldest() {
      // we will assume, that the history is ordered descending by "CREATED_AT"
      const length = this.filteredHistory.length
      return this.filteredHistory?.[length - 1]
    }
  },

  watch: {
    history: {
      handler(value) {
        this.internalHistory = value
      },
      immediate: true
    }
  },

  methods: {
    refetch() {
      this.$apollo.queries.internalHistory.refetch()
    }
  }
}
</script>
